/**
 * B523 — the booking sheet's shell (368-2 / 370-2 / 370-61): the header
 * with the step's title and the numbered trail, the ✕ that posts `cancel`
 * to the host, and the hold strip the moment a hold exists. Drawn by the
 * STOREFRONT — inside the frame — so every piece wears the tenant theme
 * and the frame's width (the loader keeps only a fallback ✕ until ready).
 *
 * The trail is a nav for COMPLETED steps only (the item's rule). "Day &
 * time" completes with the tickets step: on the wide sheet both live on
 * one screen (368-2 draws the steppers left and the calendar right), so
 * the trail never holds it active — it turns done when a departure is
 * picked. The Done face drops the trail: nothing is left to navigate.
 */
import type { ReactElement, ReactNode } from "react";
import { HoldStrip, SheetHead, StepTrail } from "@transact/ui";

export type DialogStep = "tickets" | "cart" | "pay" | "done";

const TRAIL = [
  { key: "tickets", label: "Tickets" },
  { key: "when", label: "Day & time" },
  { key: "cart", label: "Cart" },
  { key: "pay", label: "Pay" },
] as const;

/** The trail's active key per step — the tickets screen holds steps 1–2. */
const ACTIVE: Record<Exclude<DialogStep, "done">, string> = {
  tickets: "tickets",
  cart: "cart",
  pay: "pay",
};

const TITLE: Record<DialogStep, string> = {
  tickets: "Tickets",
  cart: "Your cart",
  pay: "Pay",
  done: "You're booked",
};

export function DialogShell(props: {
  readonly step: DialogStep;
  /** ‹ in the header — absent on the first step (nowhere back to go). */
  readonly onBack?: (() => void) | undefined;
  /** A completed trail step pressed — never fired for current or future. */
  readonly onVisit?: ((key: string) => void) | undefined;
  /** The ✕ — posts `cancel` through the bridge; the host closes the sheet. */
  readonly onClose: () => void;
  /** Milliseconds left on the live hold; absent = no hold, no strip (§10). */
  readonly holdMsLeft?: number | undefined;
  readonly children?: ReactNode;
}): ReactElement {
  return (
    <div className="dialog-shell" data-redline="dialog-shell">
      <SheetHead
        title={TITLE[props.step]}
        redline="dialog-shell-head"
        {...(props.onBack !== undefined ? { onBack: props.onBack } : {})}
        onClose={props.onClose}
        {...(props.step === "done"
          ? {}
          : {
              center: (
                <StepTrail
                  steps={TRAIL}
                  activeKey={ACTIVE[props.step]}
                  {...(props.onVisit !== undefined ? { onVisit: props.onVisit } : {})}
                  redline="dialog-shell-trail"
                />
              ),
            })}
      />
      {props.holdMsLeft !== undefined && props.holdMsLeft > 0 ? (
        <HoldStrip msLeft={props.holdMsLeft} redline="dialog-shell-hold" />
      ) : null}
      {props.children !== undefined ? <div className="dialog-shell-content">{props.children}</div> : null}
    </div>
  );
}
